import {
  clearComposePlan,
  hydrateComposePlan,
  newComposePlan,
  saveComposePlan,
  type ComposeEntryMode,
  type ComposePlan,
  type ComposePlanSummary,
} from "@/lib/manufacturing/composePlan";
import {
  cancelManufacturingComposePlan,
  completeManufacturingComposePlan,
  createManufacturingComposePlan,
  listManufacturingComposePlans,
  resumeManufacturingComposePlan,
  saveManufacturingComposePlan,
  type MfgComposePlanRemoteSummary,
} from "@/lib/manufacturing/composePlansClient";

export function summaryFromRemote(row: MfgComposePlanRemoteSummary): ComposePlanSummary {
  return {
    planId: row.id,
    seriesName: row.series_name,
    seriesCode: row.series_code,
    entryMode: row.entry_mode,
    step: (row.step as ComposePlan["step"]) || "define",
    updatedAt: row.updated_at,
    productCount: row.product_count,
    committedProducts: row.committed_products,
    readyProducts: row.ready_products,
    status: row.status,
    updatedByUserId: row.updated_by_user_id,
  };
}

export async function fetchComposePlanSummaries(): Promise<
  { ok: true; plans: ComposePlanSummary[] } | { ok: false; error: string }
> {
  const res = await listManufacturingComposePlans({ handoff_only: true });
  if (!res.ok) return { ok: false, error: res.error };
  return { ok: true, plans: res.data.plans.map(summaryFromRemote) };
}

export async function persistComposePlanRemote(
  plan: ComposePlan,
): Promise<{ ok: true } | { ok: false; error: string }> {
  saveComposePlan(plan);
  const res = await saveManufacturingComposePlan({
    plan_id: plan.planId,
    plan,
  });
  if (!res.ok) return { ok: false, error: res.error };
  return { ok: true };
}

export async function createComposePlanRemote(input: {
  orgId: string;
  entryMode: ComposeEntryMode;
  recipeGroupId?: string | null;
  seriesCode?: string;
  seriesName?: string;
  seedPlan?: ComposePlan;
}): Promise<{ ok: true; plan: ComposePlan } | { ok: false; error: string }> {
  const seed =
    input.seedPlan ??
    newComposePlan({
      orgId: input.orgId,
      entryMode: input.entryMode,
      archetype: {
        recipe_group_id: input.recipeGroupId ?? null,
        seriesCode: input.seriesCode ?? "",
        seriesName: input.seriesName ?? "",
      },
    });
  const res = await createManufacturingComposePlan({
    entry_mode: input.entryMode,
    recipe_group_id: input.recipeGroupId ?? seed.archetype.recipe_group_id,
    series_name: input.seriesName ?? seed.archetype.seriesName,
    series_code: input.seriesCode ?? seed.archetype.seriesCode,
    plan: seed,
  });
  if (!res.ok) return { ok: false, error: res.error };
  const hydrated = hydrateComposePlan(res.data.plan.plan, {
    orgId: input.orgId,
    planId: res.data.plan.id,
  });
  if (!hydrated) return { ok: false, error: "Created plan was invalid." };
  return {
    ok: true,
    plan: withRowGroup(hydrated, res.data.plan.recipe_group_id),
  };
}

function withRowGroup(plan: ComposePlan, rowGroupId: string | null | undefined): ComposePlan {
  if (plan.archetype.recipe_group_id) return plan;
  if (typeof rowGroupId !== "string" || !rowGroupId.trim()) return plan;
  return {
    ...plan,
    archetype: { ...plan.archetype, recipe_group_id: rowGroupId.trim() },
  };
}

export async function resumeComposePlanRemote(
  orgId: string,
  planId: string,
): Promise<{ ok: true; plan: ComposePlan } | { ok: false; error: string }> {
  const res = await resumeManufacturingComposePlan(planId);
  if (!res.ok) return { ok: false, error: res.error };
  const hydrated = hydrateComposePlan(res.data.plan.plan, {
    orgId,
    planId: res.data.plan.id,
  });
  if (!hydrated) return { ok: false, error: "Could not load saved plan." };
  return { ok: true, plan: withRowGroup(hydrated, res.data.plan.recipe_group_id) };
}

export async function discardComposePlanRemote(
  orgId: string,
  planId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await cancelManufacturingComposePlan(planId);
  clearComposePlan(orgId, planId);
  if (!res.ok) return { ok: false, error: res.error };
  return { ok: true };
}

export async function completeComposePlanRemote(
  plan: ComposePlan,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await completeManufacturingComposePlan({
    plan_id: plan.planId,
    plan,
  });
  clearComposePlan(plan.orgId, plan.planId);
  if (!res.ok) return { ok: false, error: res.error };
  return { ok: true };
}

export async function importComposePlanRemote(
  orgId: string,
  raw: string,
): Promise<{ ok: true; plan: ComposePlan } | { ok: false; error: string }> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: "Invalid compose plan JSON." };
  }
  const seed = hydrateComposePlan(parsed, { orgId, planId: "import-pending" });
  if (!seed) return { ok: false, error: "Invalid compose plan JSON." };
  return createComposePlanRemote({
    orgId,
    entryMode: seed.entryMode,
    recipeGroupId: seed.archetype.recipe_group_id,
    seriesName: seed.archetype.seriesName,
    seriesCode: seed.archetype.seriesCode,
    seedPlan: seed,
  });
}
